/* ─────────────────────────────────────────────
   POSSEN — Client Looks lookbook (virtual scroll)
   + in-page detail overlay (expand from clicked tile)
   Looks are rendered server-side from the Inspirations collection
   (sections/client-looks-grid.liquid → handle `inspirations`).
   ───────────────────────────────────────────── */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var STAGGER = [0.0, 0.37, 0.71, 0.18, 0.55, 0.83, 0.42, 0.07];
  var LINE_HEIGHT = 16;
  var PAGE_HEIGHT = 800;
  var WHEEL_FACTOR = 1.0;
  var OPEN_MS = REDUCED ? 0 : 950;
  var CLOSE_MS = REDUCED ? 0 : 500;
  var CONTENT_OUT_MS = REDUCED ? 0 : 200;
  var PUSH_FRACTION = 0.55;
  var STAGE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  function buildStageTransition(ms) {
    var s = (ms / 1000) + 's';
    return 'top ' + s + ' ' + STAGE_EASE + ', ' +
           'left ' + s + ' ' + STAGE_EASE + ', ' +
           'width ' + s + ' ' + STAGE_EASE + ', ' +
           'height ' + s + ' ' + STAGE_EASE + ', ' +
           'border-radius ' + s + ' ' + STAGE_EASE;
  }
  var STAGE_TRANSITION_OPEN = buildStageTransition(OPEN_MS);
  var STAGE_TRANSITION_CLOSE = buildStageTransition(CLOSE_MS);

  var entries = [];
  var virtualScroll = 0;
  var ticking = false;
  var detailOpen = false;
  var looksById = {};
  var detailEl = null;
  var stageEl = null;
  var mediaEl = null;
  var panelEl = null;
  var lastFocus = null;
  var activeSourceCard = null;
  var animTimer = null;

  function getBreakpoint() {
    if (window.matchMedia('(max-width: 768px)').matches) return 'mobile';
    if (window.matchMedia('(max-width: 1024px)').matches) return 'tablet';
    return 'desktop';
  }

  function activeColSelector() {
    var bp = getBreakpoint();
    if (bp === 'mobile') return '[data-cl-col-mobile]';
    if (bp === 'tablet') return '[data-cl-col-tablet]';
    return '[data-cl-col-desktop]';
  }

  function buildCols(grid) {
    var colEls = grid.querySelectorAll(activeColSelector());
    return Array.prototype.map.call(colEls, function (el, i) {
      var track = el.querySelector('[data-cl-track]');
      var raw = parseFloat(el.dataset.speed);
      if (isNaN(raw)) raw = 1.0;
      return {
        el: el,
        track: track,
        speed: raw,
        stagger: STAGGER[i % STAGGER.length],
        setHeight: 0
      };
    });
  }

  function init() {
    var sections = document.querySelectorAll('[data-cl-section]');
    if (!sections.length) return;
    Array.prototype.forEach.call(sections, setupSection);
    loadLooksData(sections[0]);
    bindDetail();

    if (REDUCED) return;

    bindInput();
    requestUpdate();

    window.addEventListener('resize', debounce(function () {
      entries.forEach(function (entry) {
        entry.cols = buildCols(entry.grid);
      });
      recalcAll();
      requestUpdate();
    }, 150));
    window.addEventListener('load', function () {
      recalcAll();
      requestUpdate();
    });
  }

  function loadLooksData(section) {
    var script = section.querySelector('[data-cl-looks-json]');
    if (!script) return;
    try {
      var data = JSON.parse(script.textContent);
      if (!data || !data.looks) return;
      data.looks.forEach(function (look) {
        looksById[String(look.id)] = look;
      });
    } catch (e) {
      /* ignore malformed JSON */
    }
  }

  function bindDetail() {
    document.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-cl-open]');
      if (openBtn && !openBtn.hasAttribute('aria-hidden')) {
        e.preventDefault();
        openDetail(openBtn.dataset.lookId, openBtn);
        return;
      }
      var closeBtn = e.target.closest('[data-cl-close]');
      if (closeBtn && detailOpen) {
        e.preventDefault();
        closeDetail();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && detailOpen) {
        e.preventDefault();
        closeDetail();
      }
    });
  }

  function openDetail(lookId, sourceBtn) {
    if (!detailEl || !stageEl || detailOpen) return;
    var look = looksById[String(lookId)];
    if (!look) return;

    lastFocus = sourceBtn || document.activeElement;
    activeSourceCard = sourceBtn || null;
    detailOpen = true;

    document.body.classList.remove('client-looks-detail-closing');

    renderDetail(look);
    detailEl.hidden = false;
    detailEl.setAttribute('aria-hidden', 'false');
    stageEl.hidden = false;

    var closeBtn = detailEl.querySelector('[data-cl-close]');
    if (closeBtn) closeBtn.focus();

    // Lock hero aspect to the clicked tile so cover crop stays consistent through the morph.
    if (mediaEl && sourceBtn) {
      var srcRect = sourceBtn.getBoundingClientRect();
      if (srcRect.width > 0 && srcRect.height > 0) {
        mediaEl.style.setProperty(
          '--cl-media-aspect',
          (srcRect.width / srcRect.height).toFixed(4)
        );
      }
    }

    if (REDUCED || !sourceBtn) {
      document.body.classList.add('client-looks-detail-open');
      setStageFullscreen(true);
      return;
    }

    var fromRect = sourceBtn.getBoundingClientRect();
    if (!fromRect.width) {
      document.body.classList.add('client-looks-detail-open');
      setStageFullscreen(true);
      return;
    }

    applyGridPush(sourceBtn);
    detailEl.classList.add('is-animating');
    setStageRect(fromRect, true);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        document.body.classList.add('client-looks-detail-open');
        setStageFullscreen(false);
      });
    });

    clearTimeout(animTimer);
    animTimer = setTimeout(function () {
      detailEl.classList.remove('is-animating');
    }, OPEN_MS);
  }

  function closeDetail() {
    if (!detailEl || !detailOpen) return;

    var sourceBtn = lastFocus;
    detailOpen = false;
    document.body.classList.remove('client-looks-detail-open');
    document.body.classList.add('client-looks-detail-closing');
    detailEl.classList.add('is-animating');

    clearTimeout(animTimer);

    if (REDUCED || !stageEl || !sourceBtn) {
      finishClose();
      return;
    }

    var toRect = sourceBtn.getBoundingClientRect();
    if (!toRect.width) {
      finishClose();
      return;
    }

    // Re-sync tile glide-back to the stage close duration so they recall together.
    var section = sourceBtn.closest('[data-cl-section]');
    if (section) {
      section.style.setProperty('--cl-tile-transform-ms', CLOSE_MS + 'ms');
      section.style.setProperty('--cl-tile-opacity-ms', Math.round(CLOSE_MS * 0.78) + 'ms');
    }

    // Phase 1: content fades out (CSS-driven via .client-looks-detail-closing).
    // Phase 2: after content is gone, the stage shrinks back and tiles recall.
    animTimer = setTimeout(function () {
      resetGridPush(true);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          setStageRect(toRect, false);
        });
      });
      animTimer = setTimeout(finishClose, CLOSE_MS);
    }, CONTENT_OUT_MS);
  }

  function finishClose() {
    resetGridPush();
    resetStageStyles();
    detailEl.classList.remove('is-animating');
    detailEl.setAttribute('aria-hidden', 'true');
    detailEl.hidden = true;
    if (stageEl) stageEl.hidden = true;
    if (mediaEl) {
      mediaEl.innerHTML = '';
      mediaEl.style.removeProperty('--cl-media-aspect');
    }
    if (panelEl) panelEl.innerHTML = '';
    document.body.classList.remove('client-looks-detail-closing');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
    activeSourceCard = null;
  }

  function setStageRect(rect, instant) {
    if (!stageEl) return;
    stageEl.style.transition = instant ? 'none' : STAGE_TRANSITION_CLOSE;
    stageEl.style.top = rect.top + 'px';
    stageEl.style.left = rect.left + 'px';
    stageEl.style.width = rect.width + 'px';
    stageEl.style.height = rect.height + 'px';
    stageEl.style.borderRadius = '2px';
    if (instant) {
      stageEl.offsetHeight;
    }
  }

  function setStageFullscreen(instant) {
    if (!stageEl) return;
    stageEl.style.transition = instant ? 'none' : STAGE_TRANSITION_OPEN;
    stageEl.style.top = '0';
    stageEl.style.left = '0';
    stageEl.style.width = '100%';
    stageEl.style.height = '100%';
    stageEl.style.borderRadius = '0';
    if (instant) {
      stageEl.offsetHeight;
      stageEl.style.transition = STAGE_TRANSITION_OPEN;
    }
  }

  function resetStageStyles() {
    if (!stageEl) return;
    stageEl.style.top = '';
    stageEl.style.left = '';
    stageEl.style.width = '';
    stageEl.style.height = '';
    stageEl.style.borderRadius = '';
    stageEl.style.transition = '';
  }

  function applyGridPush(sourceBtn) {
    resetGridPush();
    if (!sourceBtn) return;

    var section = sourceBtn.closest('[data-cl-section]');
    if (!section) return;

    activeSourceCard = sourceBtn;
    sourceBtn.classList.add('is-expansion-source');
    section.classList.add('is-grid-receding');

    // Sync the tile push duration to the stage open duration so they travel together.
    section.style.setProperty('--cl-tile-transform-ms', OPEN_MS + 'ms');
    section.style.setProperty('--cl-tile-opacity-ms', Math.round(OPEN_MS * 0.78) + 'ms');

    var origin = sourceBtn.getBoundingClientRect();
    var ox = origin.left + origin.width / 2;
    var oy = origin.top + origin.height / 2;
    var push = Math.max(window.innerWidth, window.innerHeight) * PUSH_FRACTION;

    var cards = section.querySelectorAll(activeColSelector() + ' .client-looks-card');
    Array.prototype.forEach.call(cards, function (card) {
      if (card === sourceBtn) return;

      var r = card.getBoundingClientRect();
      var cx = r.left + r.width / 2;
      var cy = r.top + r.height / 2;
      var dx = cx - ox;
      var dy = cy - oy;
      var dist = Math.sqrt(dx * dx + dy * dy) || 1;
      var nx = (dx / dist) * push;
      var ny = (dy / dist) * push;

      card.style.setProperty('--cl-push-x', nx.toFixed(2) + 'px');
      card.style.setProperty('--cl-push-y', ny.toFixed(2) + 'px');
      card.classList.add('is-pushed-away');
    });
  }

  function resetGridPush(keepRecedingClass) {
    if (activeSourceCard) {
      activeSourceCard.classList.remove('is-expansion-source');
      activeSourceCard = null;
    }

    var pushed = document.querySelectorAll('.client-looks-card.is-pushed-away');
    Array.prototype.forEach.call(pushed, function (card) {
      card.classList.remove('is-pushed-away');
      card.style.removeProperty('--cl-push-x');
      card.style.removeProperty('--cl-push-y');
    });

    if (keepRecedingClass) return;

    var sections = document.querySelectorAll('[data-cl-section].is-grid-receding');
    Array.prototype.forEach.call(sections, function (section) {
      section.classList.remove('is-grid-receding');
    });
  }

  function renderDetail(look) {
    if (!mediaEl || !panelEl) return;

    var imgHtml = '';
    if (look.image && look.image.src) {
      imgHtml =
        '<figure class="client-looks-detail__hero">' +
        '<img class="client-looks-detail__hero-img" src="' + esc(look.image.src) + '"' +
        (look.image.srcset ? ' srcset="' + esc(look.image.srcset) + '"' : '') +
        ' sizes="(max-width: 900px) 100vw, 55vw"' +
        ' alt="' + esc(look.image.alt || look.title || '') + '"' +
        ' decoding="async" />' +
        '</figure>';
    }

    var productCount = look.productCount || 0;
    var productsHtml = getShopHtml(look.id);
    var countLabel =
      productCount +
      ' piece' +
      (productCount === 1 ? '' : 's');

    mediaEl.innerHTML = imgHtml;

    panelEl.innerHTML =
      '<div class="client-looks-detail__panel-inner">' +
      '<section class="client-looks-detail__shop products-list" aria-labelledby="client-looks-detail-title">' +
      '<div class="client-looks-detail__shop-bar">' +
      '<h2 id="client-looks-detail-title" class="client-looks-detail__shop-title">Shop the look</h2>' +
      (productCount
        ? '<span class="client-looks-detail__shop-count">' + esc(countLabel) + '</span>'
        : '') +
      '</div>' +
      '<div class="client-looks-detail__shop-body">' +
      productsHtml +
      '</div>' +
      '</section>' +
      '</div>';
  }

  function getShopHtml(lookId) {
    var tpl = document.querySelector(
      'template[data-cl-look-shop][data-look-id="' + String(lookId) + '"]'
    );
    if (tpl && tpl.innerHTML.trim()) {
      return tpl.innerHTML;
    }
    return (
      '<p class="product-grid-empty client-looks-detail__empty">' +
      'Products for this look are coming soon. Add references to the ' +
      '<strong>Featured products</strong> metafield in the Shopify admin.' +
      '</p>'
    );
  }

  function esc(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function setupSection(section) {
    detailEl = section.querySelector('[data-cl-detail]');
    if (detailEl && detailEl.parentNode !== document.body) {
      document.body.appendChild(detailEl);
    }
    stageEl = detailEl && detailEl.querySelector('[data-cl-stage]');
    mediaEl = detailEl && detailEl.querySelector('[data-cl-detail-media]');
    panelEl = detailEl && detailEl.querySelector('[data-cl-detail-panel]');

    var grid = section.querySelector('[data-cl-grid]');
    if (!grid) return;
    var density = parseInt(grid.dataset.loopDensity || '3', 10);
    if (isNaN(density) || density < 2) density = 2;

    var entry = { section: section, grid: grid, cols: buildCols(grid), density: density };
    entries.push(entry);
    recalc(entry);

    var imgs = grid.querySelectorAll('img');
    Array.prototype.forEach.call(imgs, function (img) {
      if (!img.complete) {
        img.addEventListener('load', function () {
          recalc(entry);
          requestUpdate();
        }, { once: true });
        img.addEventListener('error', function () {
          recalc(entry);
          requestUpdate();
        }, { once: true });
      }
    });
  }

  function recalc(entry) {
    entry.cols.forEach(function (c) {
      if (!c.track) {
        c.setHeight = 0;
        return;
      }
      var children = c.track.children;
      if (!children.length) {
        c.setHeight = 0;
        return;
      }
      var cardsPerSet = Math.floor(children.length / entry.density);
      if (cardsPerSet < 1) {
        c.setHeight = 0;
        return;
      }
      var anchor = children[cardsPerSet];
      c.setHeight = anchor ? anchor.offsetTop : 0;
    });
  }

  function recalcAll() {
    entries.forEach(recalc);
  }

  function bindInput() {
    document.addEventListener(
      'wheel',
      function (e) {
        if (detailOpen) return;
        if (!isOnLookbook(e.target)) return;
        e.preventDefault();
        var delta = e.deltaY;
        if (e.deltaMode === 1) delta *= LINE_HEIGHT;
        else if (e.deltaMode === 2) delta *= PAGE_HEIGHT;
        virtualScroll += delta * WHEEL_FACTOR;
        requestUpdate();
      },
      { passive: false }
    );

    var touchY = null;
    document.addEventListener('touchstart', function (e) {
      if (detailOpen) return;
      if (!isOnLookbook(e.target)) return;
      touchY = e.touches[0].clientY;
    }, { passive: true });

    document.addEventListener('touchmove', function (e) {
      if (detailOpen) return;
      if (!isOnLookbook(e.target)) return;
      if (touchY === null) return;
      e.preventDefault();
      var newY = e.touches[0].clientY;
      virtualScroll += (touchY - newY);
      touchY = newY;
      requestUpdate();
    }, { passive: false });

    document.addEventListener('touchend', function () { touchY = null; }, { passive: true });
    document.addEventListener('touchcancel', function () { touchY = null; }, { passive: true });

    document.addEventListener('keydown', function (e) {
      if (detailOpen) return;
      var tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      var step = window.innerHeight * 0.4;
      switch (e.key) {
        case 'ArrowDown':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          virtualScroll += e.key === 'PageDown' ? step * 2 : step;
          requestUpdate();
          break;
        case 'ArrowUp':
        case 'PageUp':
          e.preventDefault();
          virtualScroll -= e.key === 'PageUp' ? step * 2 : step;
          requestUpdate();
          break;
      }
    });
  }

  function isOnLookbook(target) {
    if (!target) return false;
    if (target.closest && target.closest('header')) return false;
    if (target.closest && target.closest('[data-cl-detail]')) return false;
    return document.body.classList.contains('template--client-looks');
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  function update() {
    ticking = false;
    if (REDUCED || detailOpen) return;

    entries.forEach(function (entry) {
      entry.cols.forEach(function (c) {
        if (!c.track || !c.setHeight) {
          if (c.track) c.track.style.transform = '';
          return;
        }
        var raw = c.stagger * c.setHeight + virtualScroll * c.speed;
        var offset = ((raw % c.setHeight) + c.setHeight) % c.setHeight;
        c.track.style.transform =
          'translate3d(0, ' + (-offset).toFixed(2) + 'px, 0)';
      });
    });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
